"use client";

import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import {
  LAMPORTS_PER_SOL,
  PublicKey,
  clusterApiUrl,
  Transaction,
  SystemProgram,
} from "@solana/web3.js";
import {
  useState,
  useEffect,
  SetStateAction,
  useCallback,
  useMemo,
  memo,
} from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Loader2,
  ArrowLeft,
  Info,
  ExternalLink,
  Wifi,
  WifiOff,
  X,
  Settings,
  History,
  Trash2,
  Sparkles,
} from "lucide-react";
import Image from "next/image";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import dynamic from "next/dynamic";
import { useWindowSize } from "react-use";

import "@solana/wallet-adapter-react-ui/styles.css";
import { useRPC } from "@/providers/SolanaProvider";
import { RPCSelector } from "@/components/rpc-selector";
import { RPCStatus } from "@/components/rpc-status";
import { BalanceDisplay } from "@/components/balance-display";
import { AmountSelector } from "@/components/amount-selector";
import { OnesieTwosieMode } from "@/components/onesie-twosie-mode";
import { AirdropButton } from "@/components/airdrop-button";
import { ScannerSelector } from "@/components/scanner-selector";
import { WalletManagerPanel } from "@/components/wallet-manager-panel";

const BackgroundGradient = dynamic(
  () =>
    import("@/components/background-gradient").then(
      (mod) => mod.BackgroundGradient
    ),
  {
    ssr: false,
    loading: () => <div className="hidden">Loading...</div>,
  }
);

const HistoryModal = dynamic(
  () => import("@/components/history-modal").then((mod) => mod.HistoryModal),
  {
    ssr: false,
    loading: () => <div className="hidden">Loading...</div>,
  }
);

const ErrorDetailsModal = dynamic(
  () =>
    import("@/components/error-details-modal").then(
      (mod) => mod.ErrorDetailsModal
    ),
  {
    ssr: false,
    loading: () => <div className="hidden">Loading...</div>,
  }
);

const CustomRPCModal = dynamic(
  () =>
    import("@/components/custom-rpc-modal").then((mod) => mod.CustomRPCModal),
  {
    ssr: false,
    loading: () => <div className="hidden">Loading...</div>,
  }
);

const ReactConfetti = dynamic(() => import("react-confetti"), {
  ssr: false,
});

const MemoizedBalanceDisplay = memo(BalanceDisplay);
const MemoizedAmountSelector = memo(AmountSelector);
const MemoizedOnesieTwosieMode = memo(OnesieTwosieMode);
const MemoizedAirdropButton = memo(AirdropButton);

interface TransactionHistoryItem {
  id: string;
  timestamp: string;
  address: string;
  amount: number;
  rpc: string;
  status: "success" | "failed";
  signature?: string;
  error?: string;
}

type Scanner = {
  name: string;
  baseUrl: string;
  icon?: string; // We could add icons later if needed
};

const SCANNERS: Scanner[] = [
  {
    name: "Solscan",
    baseUrl: "https://solscan.io",
  },
  {
    name: "Solana FM",
    baseUrl: "https://solana.fm",
  },
  {
    name: "Solana Explorer",
    baseUrl: "https://explorer.solana.com",
  },
  {
    name: "XRAY",
    baseUrl: "https://xray.helius.xyz",
  },
];

const RATE_LIMIT_DELAY = 2000; // 2 seconds between transactions

export function usePersistentState<T>(
  key: string,
  defaultValue: T
): [T, (value: SetStateAction<T>) => void] {
  const [state, setState] = useState<T>(() => {
    if (typeof window === "undefined") return defaultValue;

    try {
      const item = localStorage.getItem(key);
      if (!item) return defaultValue;

      return JSON.parse(item) as T;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      if (state === null || state === undefined) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(state));
      }
    } catch (error) {
      console.error(`Error saving state for key ${key}:`, error);
    }
  }, [key, state]);

  return [state, setState];
}

export function Faucet() {
  const { connection } = useConnection();
  const { endpoint, setEndpoint } = useRPC();
  const { publicKey, sendTransaction } = useWallet();
  const [address, setAddress] = usePersistentState("lastAddress", "");
  const [selectedRpc, setSelectedRpc] = usePersistentState(
    "selectedRpcOption",
    "devnet"
  );
  const [customRpc, setCustomRpc] = usePersistentState("customRpcUrl", "");
  const [amount, setAmount] = usePersistentState("lastAmount", 1);
  const [oneByOne, setOneByOne] = usePersistentState("oneByOne", false);
  const [isCustomAmount, setIsCustomAmount] = usePersistentState(
    "isCustomAmount",
    false
  );
  const [customAmount, setCustomAmount] = usePersistentState(
    "lastCustomAmount",
    ""
  );
  const [lastRequestTime, setLastRequestTime] = usePersistentState(
    "lastRequestTime",
    null
  );
  const [recentAddresses, setRecentAddresses] = usePersistentState(
    "recentAddresses",
    []
  );
  const [transactionHistory, setTransactionHistory] = usePersistentState<
    TransactionHistoryItem[]
  >("transactionHistory", []);
  const [loading, setLoading] = useState(false);
  const [isCustomRpcModalOpen, setIsCustomRpcModalOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [balance, setBalance] = useState<number | null>(null);
  const [isValidAddress, setIsValidAddress] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [isCancelled, setIsCancelled] = useState(false);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [preferredScanner, setPreferredScanner] = usePersistentState<Scanner>(
    "preferredScanner",
    SCANNERS[0]
  );
  const [showConfetti, setShowConfetti] = useState(false);
  const { width, height } = useWindowSize();
  const [isWalletManagerOpen, setIsWalletManagerOpen] = useState(false);

  // Memoize edilmiş scanner listesi
  const scannerOptions = useMemo(
    () =>
      SCANNERS.map((scanner) => ({
        name: scanner.name,
        value: scanner.name,
        baseUrl: scanner.baseUrl,
      })),
    []
  );

  // Memoize edilmiş handler'lar
  const handleRpcChange = useCallback(
    (value: string) => {
      if (value === "custom-devnet") {
        setIsCustomRpcModalOpen(true);
        return;
      }

      setSelectedRpc(value);
      if (value === "devnet") {
        setEndpoint(clusterApiUrl("devnet"));
        setCustomRpc("");
      }
    },
    [setSelectedRpc, setEndpoint, setCustomRpc]
  );

  const handleCustomRpcSubmit = useCallback(() => {
    if (customRpc) {
      setEndpoint(customRpc);
      setSelectedRpc("custom-devnet");
      setIsCustomRpcModalOpen(false);
      toast.success("Custom RPC URL saved successfully!", { duration: 5000 });
    }
  }, [customRpc, setEndpoint, setSelectedRpc]);

  // Balance check optimizasyonu
  const checkBalance = useCallback(async () => {
    if (!connection) return;

    try {
      let targetAddress: PublicKey | null = null;

      if (address?.trim()) {
        try {
          targetAddress = new PublicKey(address.trim());
        } catch {
          // Invalid address
        }
      }

      if (!targetAddress && publicKey) {
        targetAddress = publicKey;
      }

      if (targetAddress) {
        const balance = await connection.getBalance(targetAddress);
        setBalance(balance / LAMPORTS_PER_SOL);
      } else {
        setBalance(null);
      }
    } catch (error) {
      console.error("Failed to fetch balance:", error);
      setBalance(null);
    }
  }, [connection, address, publicKey]);

  // useEffect optimizasyonu
  useEffect(() => {
    checkBalance();
    const intervalId = setInterval(checkBalance, 5000);
    return () => clearInterval(intervalId);
  }, [checkBalance]);

  useEffect(() => {
    // Only validate if there's an actual address to validate
    if (!address || address.trim() === "") {
      setIsValidAddress(true);
      return;
    }

    try {
      // Remove any whitespace and validate
      const trimmedAddress = address.trim();
      new PublicKey(trimmedAddress);
      setIsValidAddress(true);
    } catch {
      setIsValidAddress(false);
    }
  }, [address]);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const slot = await connection.getSlot();
        setIsConnected(true);
      } catch (error) {
        console.error("Connection error:", error);
        setIsConnected(false);
      }
    };

    checkConnection();
    const intervalId = setInterval(checkConnection, 10000); // Check every 10 seconds
    return () => clearInterval(intervalId);
  }, [connection]);

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Store the raw input value
    setAddress(value);
  };

  const handleAmountChange = (value: string) => {
    if (value === "custom") {
      setIsCustomAmount(true);
      return;
    }
    setIsCustomAmount(false);
    setAmount(Number(value));
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setCustomAmount(value);
      if (value !== "") {
        setAmount(Number(value));
      }
    }
  };

  const handleOneByOneChange = (checked: boolean) => {
    setOneByOne(checked);
  };

  const explorerLinks = publicKey ? (
    <div className="flex items-center gap-2 mt-1">
      <Select
        value={preferredScanner.name}
        onValueChange={(value) => {
          const scanner = SCANNERS.find((s) => s.name === value);
          if (scanner) setPreferredScanner(scanner);
        }}
      >
        <SelectTrigger className="h-6 w-[120px] bg-[#ffffff]/5 border-[#ffffff]/10 text-[#ffffff]/50 text-xs hover:bg-[#ffffff]/10">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#111111] border-[#ffffff]/10 text-white">
          {SCANNERS.map((scanner) => (
            <SelectItem
              key={scanner.name}
              value={scanner.name}
              className="text-white hover:bg-[#ffffff]/5 cursor-pointer text-xs"
            >
              {scanner.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <a
        href={`${
          preferredScanner.baseUrl
        }/account/${publicKey.toString()}?cluster=devnet`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-[#ffffff]/50 hover:text-[#ffffff]/70 flex items-center gap-1 transition-colors"
      >
        View Account
        <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  ) : null;

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  // Add history saving function
  const addToHistory = (
    item: Omit<TransactionHistoryItem, "id" | "timestamp">
  ) => {
    const newItem: TransactionHistoryItem = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      ...item,
    };

    setTransactionHistory((prev) => {
      const updated = [newItem, ...prev].slice(0, 50); // Keep last 50 transactions
      return updated;
    });
  };

  // Optimize transaction handling
  const handleTransaction = useCallback(async () => {
    if (loading) return;

    try {
      setLoading(true);
      setIsCancelled(false);
      setProgress(0);

      const targetAddress = address.trim() || publicKey?.toBase58();
      if (!targetAddress) {
        toast.error("Please connect your wallet or enter a target address");
        return;
      }

      let targetPubKey: PublicKey;
      try {
        targetPubKey = new PublicKey(targetAddress);
      } catch {
        toast.error("Invalid Solana address");
        return;
      }

      // Batch processing for better performance
      if (!oneByOne && amount > 1) {
        await processBatchAirdrop(targetPubKey, amount);
      } else {
        await processSequentialAirdrop(targetPubKey, amount);
      }
    } catch (error) {
      console.error("Airdrop failed:", error);
      handleAirdropError(error);
    } finally {
      setLoading(false);
      setProgress(0);
      setIsCancelled(false);
    }
  }, [loading, address, publicKey, amount, oneByOne, connection]);

  const copyAddress = async () => {
    if (publicKey) {
      await navigator.clipboard.writeText(publicKey.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Address copied to clipboard!");
    }
  };

  // Yeni yardımcı fonksiyonlar ekleyelim
  const processBatchAirdrop = useCallback(
    async (targetPubKey: PublicKey, amount: number) => {
      const totalLamports = LAMPORTS_PER_SOL * amount;

      try {
        const signature = await connection.requestAirdrop(
          targetPubKey,
          totalLamports
        );

        const confirmation = await connection.confirmTransaction(
          signature,
          "confirmed"
        );

        if (confirmation.value.err) {
          throw new Error("Transaction failed to confirm");
        }

        addToHistory({
          address: targetPubKey.toString(),
          amount,
          rpc: endpoint,
          status: "success",
          signature,
        });

        // Başarılı transfer sonrası
        setShowConfetti(true);
        toast.success(
          <div className="flex flex-col gap-1">
            <p className="font-medium">Transfer Successful! 🎉</p>
            <p className="text-xs text-[#ffffff]/70">
              {amount} SOL has been transferred to your account
            </p>
          </div>,
          {
            duration: 5000,
            className: "bg-gradient-to-r from-[#9945FF] to-[#00D18C]",
          }
        );

        // 5 saniye sonra confetti'yi kaldır
        setTimeout(() => setShowConfetti(false), 5000);
      } catch (error) {
        throw error;
      }
    },
    [connection, endpoint, addToHistory]
  );

  const processSequentialAirdrop = useCallback(
    async (targetPubKey: PublicKey, amount: number) => {
      let completedTransactions = 0;

      for (let i = 0; i < amount; i++) {
        if (isCancelled) {
          addToHistory({
            address: targetPubKey.toString(),
            amount: completedTransactions,
            rpc: endpoint,
            status: "failed",
            error: "Operation cancelled by user",
          });
          return;
        }

        setProgress(i + 1);

        try {
          const signature = await connection.requestAirdrop(
            targetPubKey,
            LAMPORTS_PER_SOL
          );

          const confirmation = await connection.confirmTransaction(
            signature,
            "confirmed"
          );

          if (confirmation.value.err) {
            throw new Error("Transaction failed to confirm");
          }

          completedTransactions++;

          addToHistory({
            address: targetPubKey.toString(),
            amount: 1,
            rpc: endpoint,
            status: "success",
            signature,
          });

          // Son transfer başarılı olduğunda
          if (i === amount - 1) {
            setShowConfetti(true);
            toast.success(
              <div className="flex flex-col gap-1">
                <p className="font-medium">All Transfers Completed! 🎉</p>
                <p className="text-xs text-[#ffffff]/70">
                  {amount} SOL has been transferred successfully
                </p>
              </div>,
              {
                duration: 5000,
                className: "bg-gradient-to-r from-[#9945FF] to-[#00D18C]",
              }
            );

            // 5 saniye sonra confetti'yi kaldır
            setTimeout(() => setShowConfetti(false), 5000);
          }

          if (i < amount - 1) {
            await sleep(RATE_LIMIT_DELAY);
          }
        } catch (error) {
          throw error;
        }
      }
    },
    [connection, endpoint, isCancelled, addToHistory, setProgress, sleep]
  );

  const handleAirdropError = useCallback(
    (error: any) => {
      const errorMessage = error?.message || "Unknown error occurred";
      const isRateLimit =
        errorMessage.includes("429") ||
        errorMessage.toLowerCase().includes("rate limit");

      if (isRateLimit) {
        toast.error(
          <div className="flex flex-col gap-2">
            <p>Rate limit reached or faucet is empty</p>
            <div className="flex items-center gap-2 text-xs bg-[#ffffff]/10 p-2 rounded">
              <a
                href="https://faucet.solana.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9945FF] hover:text-[#9945FF]/80 flex items-center gap-1"
              >
                Try Solana Faucet Website
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>,
          {
            duration: Infinity,
            action: {
              label: "View Details",
              onClick: () => {
                setErrorDetails(JSON.stringify(error, null, 2));
                setIsErrorModalOpen(true);
              },
            },
          }
        );
      } else {
        toast.error(
          <div className="flex flex-col gap-2">
            <p>Airdrop failed</p>
            <p className="text-xs text-[#ffffff]/70">{errorMessage}</p>
          </div>,
          {
            duration: Infinity,
            action: {
              label: "View Details",
              onClick: () => {
                setErrorDetails(JSON.stringify(error, null, 2));
                setIsErrorModalOpen(true);
              },
            },
          }
        );
      }

      addToHistory({
        address: address.trim() || publicKey?.toBase58() || "",
        amount,
        rpc: endpoint,
        status: "failed",
        error: errorMessage,
      });
    },
    [
      address,
      publicKey,
      amount,
      endpoint,
      setErrorDetails,
      setIsErrorModalOpen,
      addToHistory,
    ]
  );

  const handleSelectWallet = useCallback(
    (selectedAddress: string) => {
      setAddress(selectedAddress);
      setIsWalletManagerOpen(false);
      toast.success("Wallet selected!");
    },
    [setAddress]
  );

  return (
    <div className="relative z-10 flex flex-col items-center gap-6 sm:gap-10 w-full min-h-screen p-4 sm:p-6">
      <BackgroundGradient />

      {/* Confetti efekti */}
      {showConfetti && (
        <ReactConfetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={200}
          gravity={0.2}
          colors={["#9945FF", "#00D18C", "#ffffff"]}
          tweenDuration={5000}
        />
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-[200px] sm:w-[240px] mt-8 sm:mt-12"
      >
        <Image
          src="/solana.png"
          alt="Solana Logo"
          width={240}
          height={48}
          priority
          className="w-full h-auto drop-shadow-lg hover:drop-shadow-2xl transition-all duration-300"
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="w-full max-w-[480px] mb-8 sm:mb-12"
      >
        <Card className="bg-[#111111]/50 backdrop-blur-xl border-[#ffffff]/10 overflow-hidden">
          <CardHeader className="space-y-3 pb-4">
            <div className="flex items-center justify-between gap-4">
              <CardTitle className="text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#9945FF] to-[#00D18C]">
                Solana Devnet Faucet
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsHistoryModalOpen(true)}
                className="h-9 w-9 bg-[#ffffff]/5 hover:bg-[#ffffff]/10 transition-colors shrink-0"
              >
                <History className="h-5 w-5" />
              </Button>
            </div>
            <CardDescription className="text-[#ffffff]/70">
              Request SOL tokens for testing on Solana's devnet network.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row items-stretch gap-3">
                  <div className="flex-1">
                    <div className="flex gap-2">
                      <WalletMultiButton className="!flex-1 !h-12 !bg-gradient-to-r !from-[#ffffff]/5 !to-[#ffffff]/[0.07] hover:!from-[#ffffff]/[0.07] hover:!to-[#ffffff]/[0.09] !transition-all !duration-200 !rounded-lg !text-sm !font-medium !border !border-[#ffffff]/10" />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsWalletManagerOpen(true)}
                        className="h-12 w-12 bg-[#ffffff]/5 hover:bg-[#ffffff]/10 transition-colors shrink-0"
                      >
                        <Settings className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <ScannerSelector
                      value={preferredScanner.name}
                      onValueChange={(value) => {
                        const scanner = SCANNERS.find((s) => s.name === value);
                        if (scanner) setPreferredScanner(scanner);
                      }}
                      scanners={SCANNERS}
                    />
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 p-0 hover:bg-[#ffffff]/10 transition-colors"
                          >
                            <Info className="h-4 w-4 text-[#ffffff]/50" />
                            <span className="sr-only">Scanner information</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="right"
                          className="bg-[#111111] border-[#ffffff]/10"
                        >
                          <p className="text-xs text-white">
                            Select your preferred blockchain explorer
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>

                {balance !== null && (
                  <MemoizedBalanceDisplay
                    balance={balance}
                    address={address}
                    publicKey={publicKey?.toString() || null}
                    preferredScanner={preferredScanner}
                  />
                )}
              </div>

              <div className="space-y-3">
                <div className="relative">
                  <Input
                    placeholder="Enter Solana address"
                    value={address}
                    onChange={handleAddressChange}
                    className={cn(
                      "h-12 bg-[#ffffff]/5 border-[#ffffff]/10 text-white placeholder:text-[#ffffff]/30",
                      "focus:border-[#9945FF] focus:ring-1 focus:ring-[#9945FF]",
                      "hover:bg-[#ffffff]/[0.07] transition-colors",
                      !isValidAddress &&
                        address &&
                        "border-red-500 focus:border-red-500 focus:ring-red-500"
                    )}
                  />
                  {!isValidAddress && address && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <X className="h-4 w-4 text-red-500" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Invalid Solana address</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  )}
                </div>

                <MemoizedAmountSelector
                  amount={amount}
                  isCustomAmount={isCustomAmount}
                  customAmount={customAmount}
                  onAmountChange={handleAmountChange}
                  onCustomAmountChange={handleCustomAmountChange}
                  onBackToPreset={() => {
                    setIsCustomAmount(false);
                    setCustomAmount("");
                    setAmount(1);
                  }}
                />
              </div>

              <div className="space-y-4">
                <MemoizedOnesieTwosieMode
                  checked={oneByOne}
                  onCheckedChange={handleOneByOneChange}
                />

                <div className="space-y-3">
                  <RPCSelector
                    value={selectedRpc}
                    onValueChange={handleRpcChange}
                  />
                  <RPCStatus
                    endpoint={endpoint}
                    isConnected={isConnected}
                    selectedRpc={selectedRpc}
                    onChangeRPC={() => setIsCustomRpcModalOpen(true)}
                  />
                </div>
              </div>

              <MemoizedAirdropButton
                loading={loading}
                isCancelled={isCancelled}
                amount={amount}
                disabled={loading || (!isValidAddress && !!address)}
                onClick={handleTransaction}
              />
            </div>
          </CardContent>

          <CardFooter className="text-xs text-[#ffffff]/30 text-center px-6 py-4 border-t border-[#ffffff]/5">
            This tool is designed for development purposes and testing on
            Solana's devnet network.
          </CardFooter>
        </Card>
      </motion.div>

      <HistoryModal
        open={isHistoryModalOpen}
        onOpenChange={setIsHistoryModalOpen}
        transactionHistory={transactionHistory}
        onClearHistory={() => {
          setTransactionHistory([]);
          localStorage.removeItem("transactionHistory");
        }}
        preferredScanner={preferredScanner}
      />

      <ErrorDetailsModal
        open={isErrorModalOpen}
        onOpenChange={setIsErrorModalOpen}
        errorDetails={errorDetails}
      />

      <CustomRPCModal
        open={isCustomRpcModalOpen}
        onOpenChange={setIsCustomRpcModalOpen}
        customRpc={customRpc}
        onCustomRpcChange={setCustomRpc}
        onSave={handleCustomRpcSubmit}
      />

      <WalletManagerPanel
        isOpen={isWalletManagerOpen}
        onClose={() => setIsWalletManagerOpen(false)}
        onSelectWallet={handleSelectWallet}
      />
    </div>
  );
}
